import { FormInstance } from 'antd';
import { Rule } from 'antd/lib/form';
import dayjs from 'dayjs';
import i18n from 'i18next';
import LocalStorage from 'utils/bom-dom-manipulation/local-storage';
import {
  DEFAULT_FIAT_CURRENCY,
  DEFAULT_FIAT_CURRENCY_ID,
  MAX_MONTH_VALUE,
  YEAR_FORMAT_FOUR_DIGITS
} from 'utils/constants/account/payment';
import { PRICE_SEPARATOR_REGEXP } from 'utils/constants/price-separator';
import { EAccount } from 'utils/constants/store/query-keys';
import { currencyFormat } from 'utils/generic/currency-format';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { thousandSeparator } from 'utils/helpers/validators';
import { appendCustomCode } from 'utils/other/customCode';
import { AccountParamsType, AppendCustomCodes } from 'interfaces/generic';
import QueryClient from 'stores/query/query-client';

const { queryClient } = QueryClient;

export const isValidRegex = function (regex: string) {
  try {
    new RegExp(regex);

    return true;
  } catch (e) {
    return false;
  }
};

export const generateNumberRules = (field: Record<string, any>): Rule[] => {
  const fieldRules: Rule[] = [
    {
      required: true,
      message: `${field?.lang.replace(/:/g, '')} ${i18n.t(
        'validationMessages.isRequired'
      )}`
    }
  ];

  if (field?.regexp && isValidRegex(field?.regexp)) {
    fieldRules.push({
      validator: (_, value) => {
        if (!new RegExp(field.regexp).test(value)) {
          return Promise.reject(`${i18n.t('validationMessages.notValid')}`);
        }

        return Promise.resolve();
      }
    });
  } else {
    fieldRules.push({
      validator: (_, value) => {
        const cloneValue = value?.replaceAll(' ', '');

        if (Number.isNaN(Number(cloneValue))) {
          return Promise.reject(
            `${field.lang} ${i18n.t('validationMessages.mustBeNumber')}`
          );
        }

        if (field.minLenght && cloneValue.length < field.minLenght) {
          return Promise.reject(
            `${i18n.t('validationMessages.minimumIs', {
              field: field.lang.toLowerCase().replace(/:/g, '')
            })} ${field.minLenght}`
          );
        }

        if (field.maxLenght && cloneValue.length > field.maxLenght) {
          return Promise.reject(
            `${i18n.t('validationMessages.maximumIs', {
              field: field.lang.toLowerCase().replace(/:/g, '')
            })} ${field.maxLenght}`
          );
        }

        return Promise.resolve();
      }
    });
  }

  return fieldRules;
};

export const generateTextRules = (field: Record<string, any>): Rule[] => {
  const fieldRules: Rule[] = [
    {
      required: true,
      message: `${field?.lang.replace(/:/g, '')} ${i18n.t(
        'validationMessages.isRequired'
      )}`
    }
  ];

  if (field?.regexp && isValidRegex(field?.regexp)) {
    fieldRules.push({
      validator: (_, value) => {
        if (!new RegExp(field.regexp).test(value)) {
          return Promise.reject(`${i18n.t('validationMessages.notValid')}`);
        }

        return Promise.resolve();
      }
    });
  } else {
    fieldRules.push({
      validator: (_, value) => {
        const cloneValue = value?.replaceAll(' ', '');

        if (!/^[a-zA-Z]+$/.test(cloneValue)) {
          return Promise.reject(`${i18n.t('validationMessages.onlyLetters')}`);
        }

        return Promise.resolve();
      }
    });
  }

  return fieldRules;
};

export const generateDateRules = (
  yearFormat: number,
  form: FormInstance<any>,
  name: string
): Rule[] => {
  const fieldRules: Rule[] = [
    {
      required: true,
      message: i18n.t('account.required')
    }
  ];

  fieldRules.push({
    validator: (_, value) => {
      const date = value.split(/[/.]/);

      if (Number(date[0]) > MAX_MONTH_VALUE) {
        form.setFields([
          {
            name: [name],
            errors: [`${i18n.t('validationMessages.invalidDate')}`]
          }
        ]);

        return Promise.reject(`${i18n.t('validationMessages.invalidDate')}`);
      }

      if (
        !Number(date[1]) ||
        date[1].toString().length < yearFormat ||
        (date[1] < dayjs().year() && yearFormat === YEAR_FORMAT_FOUR_DIGITS)
      ) {
        form.setFields([
          {
            name: [name],
            errors: [`${i18n.t('validationMessages.invalidDate')}`]
          }
        ]);

        return Promise.reject(`${i18n.t('validationMessages.invalidDate')}`);
      }

      return Promise.resolve();
    }
  });

  return fieldRules;
};

export const generateRules = (
  fieldName: string,
  field: Record<string, any>,
  currency: string,
  data: Record<string, any>,
  balance?: number,
  isWithdraw?: boolean,
  phoneLength?: number
): Rule[] => {
  const lang = data.translations?.[fieldName] || field.lang || '';

  const fieldRules: Rule[] = [
    {
      required: true,
      message: `${lang.replace(/:/g, '')} ${i18n.t(
        'validationMessages.isRequired'
      )}`
    }
  ];

  const isAmount = ['eamount', 'amount', 'displayamount'].includes(fieldName);

  const isCpf = fieldName === 'accountNumber' || fieldName === 'cpf';

  const minRule = Number(data?.info_notes?.[currency]?.min);
  const maxRule = Number(data?.info_notes?.[currency]?.max);
  const currencyObj = currencyFormat(currency);
  const minLength = Number(field?.minLenght) || phoneLength;
  const maxLength = Number(field?.maxLenght) || phoneLength;

  if (field?.regexp && isValidRegex(field?.regexp)) {
    fieldRules.push({
      validator: (_, value) => {
        let val = value;

        if (isAmount) {
          val = value?.toString()?.replaceAll(PRICE_SEPARATOR_REGEXP, '');
        }

        if (!new RegExp(field.regexp).test(val)) {
          return Promise.reject(`${i18n.t('validationMessages.notValid')}`);
        }

        return Promise.resolve();
      }
    });
  } else {
    minLength &&
      fieldRules.push({
        validator: (_, value) => {
          return value.replace(/[^0-9]/g, '').length < minLength
            ? Promise.reject(
                `${lang} ${i18n.t('validationMessages.minCharacter', {
                  count: minLength
                })}`
              )
            : Promise.resolve();
        }
      });

    maxLength &&
      fieldRules.push({
        validator: (_, value) => {
          return value.replace(/[^0-9]/g, '').length > maxLength
            ? Promise.reject(
                `${lang} ${i18n.t('validationMessages.maxCharacter', {
                  count: maxLength
                })}`
              )
            : Promise.resolve();
        }
      });

    isCpf &&
      fieldRules.push({
        validator: (_, value) => {
          value = value.trim();
          const regex = /^[0-9.-]+$/;

          return !regex.test(value)
            ? Promise.reject(
                `${lang} ${i18n.t('validationMessages.mustBeNumber')}`
              )
            : Promise.resolve();
        }
      });

    (isAmount || (field?.mask && !isNaN(field?.mask))) &&
      fieldRules.push({
        validator: (_, value) => {
          if (isAmount) {
            value = value.replace(PRICE_SEPARATOR_REGEXP, '');
          }

          return Number.isNaN(Number(value))
            ? Promise.reject(
                `${lang} ${i18n.t('validationMessages.mustBeNumber')}`
              )
            : Promise.resolve();
        }
      });
    minRule &&
      isAmount &&
      fieldRules.push({
        validator: (_, value) => {
          return Number(
            value?.toString()?.replaceAll(PRICE_SEPARATOR_REGEXP, '')
          ) < minRule
            ? Promise.reject(
                `${i18n.t('validationMessages.minimumIs', {
                  field: lang.toLowerCase().replace(/:/g, '')
                })} ${
                  currencyObj.placement === 'left' ? currencyObj.currency : ''
                } ${thousandSeparator(minRule)} ${
                  currencyObj.placement === 'right' ? currencyObj.currency : ''
                }`
              )
            : Promise.resolve();
        }
      });
    maxRule &&
      isAmount &&
      fieldRules.push({
        validator: (_, value) => {
          return Number(
            value?.toString()?.replaceAll(PRICE_SEPARATOR_REGEXP, '')
          ) > maxRule
            ? Promise.reject(
                `${i18n.t('validationMessages.maximumIs', {
                  field: lang.toLowerCase().replace(/:/g, '')
                })} ${
                  currencyObj.placement === 'left' ? currencyObj.currency : ''
                } ${thousandSeparator(maxRule)} ${
                  currencyObj.placement === 'right' ? currencyObj.currency : ''
                }`
              )
            : Promise.resolve();
        }
      });

    if (fieldName === 'email') {
      fieldRules.push({
        message: i18n.t(`account.emailInput`),
        type: 'email'
      });
    }
  }

  typeof balance === 'number' &&
    isWithdraw &&
    isAmount &&
    fieldRules.push({
      validator: (_, value) => {
        return Number(
          value?.toString()?.replaceAll(PRICE_SEPARATOR_REGEXP, '')
        ) > balance
          ? Promise.reject(`${i18n.t('validationMessages.excessBalance')}`)
          : Promise.resolve();
      }
    });

  return fieldRules;
};

export const depositSuccessDataTracking = (user: Record<string, any>): void => {
  const paymentInfo = JSON.parse(
    LocalStorage.getItem(storageKeyName('account', 'PAYMENT_DATA'))
  );

  const accountParams: AccountParamsType = queryClient.getQueryData([
    EAccount['AccountParams']
  ]);

  if (accountParams) {
    if (+accountParams[AppendCustomCodes.APPEND_CUSTOM_CODE_DEPOSIT]) {
      appendCustomCode(accountParams[AppendCustomCodes.CUSTOM_CODE_DEPOSIT], {
        ...user,
        ...paymentInfo
      });
    }

    if (
      +accountParams[AppendCustomCodes.APPEND_CUSTOM_CODE_FIRST_DEPOSIT] &&
      LocalStorage.getItem(storageKeyName('account', 'FIRST_DEPOSIT')) === '1'
    ) {
      appendCustomCode(
        accountParams[AppendCustomCodes.CUSTOM_CODE_FIRST_DEPOSIT],
        {
          ...user,
          ...paymentInfo
        }
      );
      LocalStorage.setItem(storageKeyName('account', 'FIRST_DEPOSIT'), 'null');
    }
  }
};

export const getCurrencyName = (currency: string) => {
  if (currency === DEFAULT_FIAT_CURRENCY_ID) {
    return DEFAULT_FIAT_CURRENCY;
  }

  return currency;
};
